// src/components/UserNavbar.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import {
    AppBar, Toolbar, Typography, Box, IconButton, Menu, MenuItem,
    Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Avatar, Divider, ListItemIcon, Switch, Tooltip, Button
} from '@mui/material';
import {
    Menu as MenuIcon, Person, Logout, Dashboard,
    DarkMode, LightMode, Language
} from '@mui/icons-material';

const UserNavbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const theme = useTheme();
    const { isDarkMode, toggleTheme } = useCustomTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
    const [languageAnchor, setLanguageAnchor] = useState(null);

    const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleLanguageOpen = (event) => setLanguageAnchor(event.currentTarget);
    const handleLanguageClose = () => setLanguageAnchor(null);

    const handleLogoutClick = () => {
        handleMenuClose();
        setLogoutDialogOpen(true);
    };

    const handleLogoutConfirm = () => {
        setLogoutDialogOpen(false);
        logout();
        navigate('/');
    };

    const handleLogoutCancel = () => setLogoutDialogOpen(false);
    const handleMobileMenuToggle = () => setMobileMenuOpen(!mobileMenuOpen);
    const handleThemeToggle = () => toggleTheme();
    const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

    const getUserInitials = (email) => {
        if (!email) return 'D';
        return email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (user?.firstName && user?.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user?.email || 'DimitarKazz';
    };

    return (
        <>
            <AppBar
                position="static"
                sx={{
                    bgcolor: 'palette.navbar',
                    color: 'text.primary',
                    boxShadow: isDarkMode ? 'none' : '0 2px 20px rgba(0,0,0,0.1)',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: isDarkMode
                        ? 'linear-gradient(90deg, #141414 0%, #1a1a1a 100%)'
                        : 'linear-gradient(90deg, #ffffff 0%, #f8f9fa 100%)'
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>

                    {/* ✅ LOGO - Pravoagolno */}
                    <Box
                        sx={{
                            display: 'flex', alignItems: 'center', cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.05)' }
                        }}
                        onClick={() => { navigate('/user-dashboard'); scrollToTop(); }}
                    >
                        <Box
                            component="img"
                            src="/Logo.png"
                            alt="Logo"
                            sx={{
                                height: 48,
                                width: 160,
                                objectFit: 'contain',
                                //filter: isDarkMode ? 'brightness(0) invert(1)' : 'none',
                                transition: 'filter 0.3s ease'
                            }}
                        />
                    </Box>

                    {/* Right Side */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                        {/* Theme Toggle - Desktop */}
                        {!isMobile && (
                            <Tooltip title={isDarkMode ? "Светла тема" : "Темна тема"}>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 0.5,
                                    px: 1, py: 0.5, borderRadius: '20px',
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    border: `1px solid ${theme.palette.divider}`
                                }}>
                                    <LightMode sx={{
                                        fontSize: 18,
                                        color: !isDarkMode ? '#ffd166' : 'text.disabled',
                                        transition: 'all 0.3s ease'
                                    }} />
                                    <Switch
                                        checked={isDarkMode}
                                        onChange={handleThemeToggle}
                                        size="small"
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'primary.main' },
                                        }}
                                    />
                                    <DarkMode sx={{
                                        fontSize: 18,
                                        color: isDarkMode ? 'primary.main' : 'text.disabled',
                                        transition: 'all 0.3s ease'
                                    }} />
                                </Box>
                            </Tooltip>
                        )}

                        {/* Language Selector */}
                        <Tooltip title="Јазик">
                            <IconButton
                                onClick={handleLanguageOpen}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,126,185,0.1)' }
                                }}
                            >
                                <Language />
                            </IconButton>
                        </Tooltip>

                        {/* Language Menu */}
                        <Menu
                            anchorEl={languageAnchor}
                            open={Boolean(languageAnchor)}
                            onClose={handleLanguageClose}
                            PaperProps={{
                                sx: {
                                    mt: 1, bgcolor: 'background.paper', color: 'text.primary',
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: '8px', minWidth: 180,
                                    boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.8)' : '0 8px 32px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            <MenuItem onClick={handleLanguageClose} sx={{
                                bgcolor: 'rgba(255,126,185,0.1)', color: 'primary.main',
                                fontWeight: 'bold', '&:hover': { bgcolor: 'rgba(255,126,185,0.2)' }
                            }}>
                                🇲🇰 Македонски
                            </MenuItem>
                            <MenuItem disabled sx={{ opacity: 0.5 }}>🇬🇧 English (наскоро)</MenuItem>
                            <MenuItem disabled sx={{ opacity: 0.5 }}>🇦🇱 Shqip (наскоро)</MenuItem>
                        </Menu>

                        {/* User Avatar + Email */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!isMobile && (
                                <Typography variant="body2" sx={{
                                    color: 'text.secondary', fontWeight: 500,
                                    display: { xs: 'none', lg: 'block' }
                                }}>
                                    {user?.email || 'user@fitnesflix.com'}
                                </Typography>
                            )}
                            <Avatar
                                onClick={handleMenuOpen}
                                sx={{
                                    width: 36, height: 36, bgcolor: 'primary.main',
                                    cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem',
                                    border: '2px solid transparent', transition: 'all 0.3s ease',
                                    '&:hover': {
                                        border: '2px solid', borderColor: 'primary.main',
                                        transform: 'scale(1.1)',
                                        boxShadow: '0 0 15px rgba(255,126,185,0.6)'
                                    }
                                }}
                            >
                                {getUserInitials(user?.email)}
                            </Avatar>
                        </Box>

                        {/* Mobile Menu Button */}
                        {isMobile && (
                            <IconButton
                                onClick={handleMobileMenuToggle}
                                sx={{
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main', backgroundColor: 'rgba(255,126,185,0.1)' }
                                }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                    </Box>

                    {/* User Dropdown Menu */}
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                        PaperProps={{
                            sx: {
                                mt: 1, bgcolor: 'background.paper', color: 'text.primary',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '8px', minWidth: 220,
                                boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.8)' : '0 8px 32px rgba(0,0,0,0.15)',
                                '& .MuiMenuItem-root': {
                                    py: 1.5,
                                    '&:hover': { bgcolor: 'rgba(255,126,185,0.1)', color: 'primary.main' }
                                }
                            }
                        }}
                    >
                        {/* User Info Header */}
                        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle2" sx={{ color: 'text.disabled' }}>Најавен како</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {getDisplayName()}
                            </Typography>
                        </Box>

                        <MenuItem onClick={() => { navigate('/user-dashboard'); handleMenuClose(); scrollToTop(); }}>
                            <ListItemIcon><Dashboard sx={{ color: 'primary.main' }} /></ListItemIcon>
                            Мој Dashboard
                        </MenuItem>

                        <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
                            <ListItemIcon><Person sx={{ color: 'secondary.main' }} /></ListItemIcon>
                            Мој Профил
                        </MenuItem>

                        <Divider sx={{ bgcolor: theme.palette.divider, my: 1 }} />

                        <MenuItem onClick={handleLogoutClick} sx={{ color: '#ff6b6b !important' }}>
                            <ListItemIcon><Logout sx={{ color: '#ff6b6b' }} /></ListItemIcon>
                            Одјави се
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileMenuOpen}
                onClose={handleMobileMenuToggle}
                PaperProps={{
                    sx: {
                        width: 280, bgcolor: 'background.paper',
                        color: 'text.primary',
                        borderLeft: `1px solid ${theme.palette.divider}`
                    }
                }}
            >
                <Box sx={{ p: 3 }}>
                    {/* Logo vo Drawer */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box
                            component="img"
                            src="/Logo.png"
                            alt="Logo"
                            sx={{
                                height: 44,
                                width: 150,
                                objectFit: 'contain',
                                filter: isDarkMode ? 'brightness(0) invert(1)' : 'none'
                            }}
                        />
                    </Box>

                    <List>
                        {/* Theme Toggle Mobile */}
                        <ListItem
                            button
                            onClick={handleThemeToggle}
                            sx={{ borderRadius: '8px', mb: 1, '&:hover': { backgroundColor: 'rgba(255,126,185,0.1)' } }}
                        >
                            <ListItemText primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography>🎨 Тема</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LightMode sx={{ fontSize: 16, color: !isDarkMode ? '#ffd166' : 'text.disabled' }} />
                                        <Switch
                                            checked={isDarkMode}
                                            size="small"
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': { color: 'primary.main' },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'primary.main' }
                                            }}
                                        />
                                        <DarkMode sx={{ fontSize: 16, color: isDarkMode ? 'primary.main' : 'text.disabled' }} />
                                    </Box>
                                </Box>
                            } />
                        </ListItem>

                        {/* Language Mobile */}
                        <ListItem
                            button
                            sx={{ borderRadius: '8px', mb: 1, '&:hover': { backgroundColor: 'rgba(255,126,185,0.1)' } }}
                        >
                            <ListItemText primary={<Typography>🇲🇰 Македонски</Typography>} />
                        </ListItem>

                        <Divider sx={{ bgcolor: theme.palette.divider, my: 2 }} />

                        {/* User Info Mobile */}
                        <ListItem sx={{ mb: 2, bgcolor: 'rgba(255,126,185,0.1)', borderRadius: '8px' }}>
                            <ListItemText
                                primary={
                                    <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                                        👤 {getDisplayName()}
                                    </Typography>
                                }
                                secondary={
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        {user?.email || 'user@fitnesflix.com'}
                                    </Typography>
                                }
                            />
                        </ListItem>

                        <ListItem
                            button
                            onClick={() => { navigate('/user-dashboard'); setMobileMenuOpen(false); scrollToTop(); }}
                            sx={{ borderRadius: '8px', mb: 1, '&:hover': { backgroundColor: 'rgba(255,126,185,0.1)' } }}
                        >
                            <ListItemText primary={
                                <Typography sx={{ color: 'primary.main' }}>📊 Мој Dashboard</Typography>
                            } />
                        </ListItem>

                        <ListItem
                            button
                            onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                            sx={{ borderRadius: '8px', mb: 1, '&:hover': { backgroundColor: 'rgba(255,126,185,0.1)' } }}
                        >
                            <ListItemText primary={<Typography>👤 Мој Профил</Typography>} />
                        </ListItem>

                        <Divider sx={{ bgcolor: theme.palette.divider, my: 1 }} />

                        <ListItem
                            button
                            onClick={() => setLogoutDialogOpen(true)}
                            sx={{ borderRadius: '8px', color: '#ff6b6b', '&:hover': { backgroundColor: 'rgba(255,107,107,0.1)' } }}
                        >
                            <ListItemText primary="🚪 Одјави се" />
                        </ListItem>
                    </List>
                </Box>
            </Drawer>

            {/* Logout Dialog */}
            <Dialog
                open={logoutDialogOpen}
                onClose={handleLogoutCancel}
                PaperProps={{
                    sx: {
                        bgcolor: 'background.paper', color: 'text.primary',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '12px', minWidth: '420px',
                        boxShadow: isDarkMode ? '0 8px 32px rgba(0,0,0,0.8)' : '0 8px 32px rgba(0,0,0,0.15)'
                    }
                }}
            >
                <DialogTitle sx={{
                    color: 'primary.main', fontWeight: 'bold',
                    textAlign: 'center', fontSize: '1.5rem', pb: 1
                }}>
                    🔐 Потврда за одјава
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', py: 3 }}>
                    <DialogContentText sx={{ color: 'text.secondary', fontSize: '1.1rem', lineHeight: 1.6 }}>
                        Дали сте сигурни дека сакате да се одјавите од
                        <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}> FITNESFLIX</Box>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button
                        onClick={handleLogoutCancel}
                        variant="outlined"
                        sx={{
                            color: 'text.secondary', borderColor: theme.palette.divider,
                            fontWeight: 'bold', px: 4, py: 1.2, borderRadius: '8px',
                            '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'rgba(255,126,185,0.1)',
                                color: 'primary.main'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Откажи
                    </Button>
                    <Button
                        onClick={handleLogoutConfirm}
                        variant="contained"
                        sx={{
                            bgcolor: 'primary.main', color: 'white',
                            fontWeight: 'bold', px: 4, py: 1.2, borderRadius: '8px',
                            '&:hover': {
                                bgcolor: 'primary.dark',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 20px rgba(255,126,185,0.4)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        Одјави се
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default UserNavbar;